import { useMemo, useState } from 'react';
import { Input, Table } from 'antd';
import { Button, Form, SIZE, Spinner, Stack, Text, useForm } from '@quinyx/ui';
import * as z from 'zod';

import {
  MAX_DOMAINS_FOR_DROPDOWN,
  useCustomerManagerTokens,
  useDeleteCustomerManagerToken,
  useGenerateCustomerManagerToken,
  useManageableDomains,
  useManageableDomainsCount,
  useSearchManageableDomains,
} from '@/hooks/customerManagerToken';
import { useGetCustomer } from '@/hooks/customer';
import { formatDateTime } from '@/utils/time';
import { withAdminLayout } from '../../../layouts/admin';
import { useTranslate } from '../../../hooks/translate';
import styles from './index.module.scss';

const schema = z.object({
  domainId: z.string().min(1),
});

interface ITokenRow {
  key: string;
  domainId: number;
  token: string;
  expiresAt: string;
}

const toDomainOptionValue = (domainId: number) => `domain-${domainId}`;
const fromDomainOptionValue = (optionValue: string) =>
  Number(optionValue.replace(/^domain-/, ''));

const TenantNameCell = ({
  domainId,
  knownName,
}: {
  domainId: number;
  knownName?: string;
}) => {
  const { data: customer } = useGetCustomer(String(domainId), !knownName);
  return <>{knownName ?? customer?.name ?? domainId}</>;
};

const TenantSearch = ({
  onSelect,
  disabled,
}: {
  onSelect: (domainId: number) => Promise<unknown>;
  disabled?: boolean;
}) => {
  const { t } = useTranslate();
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const {
    data: results,
    isFetching,
    isError,
  } = useSearchManageableDomains(query);

  const handleSearch = () => setQuery(inputValue.trim());

  const handleReset = () => {
    setInputValue('');
    setQuery('');
  };

  const handleSelect = async (domainId: number) => {
    setIsGenerating(true);
    try {
      await onSelect(domainId);
      handleReset();
    } catch {
      // Error toast is already shown by the mutation itself — keep the
      // search results visible so the user can try a different tenant.
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Stack spacing={SIZE.S}>
      <Text variant="bodyRegular">
        {t('admin.customerManagerToken.tenant')}
      </Text>
      <Stack
        direction="row"
        spacing={SIZE.XS}
        alignItems="center"
        className={styles.searchRow}>
        <Input
          data-test-id="customer-manager-token-domain-search-input"
          className={styles.searchInput}
          placeholder={t('admin.customerManagerToken.tenantSearchPlaceholder')}
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onPressEnter={handleSearch}
        />
        <Button
          type="button"
          data-test-id="customer-manager-token-domain-search-button"
          onClick={handleSearch}
          disabled={isGenerating}
          leftIcon="search"
          text={t('common.search')}
        />
        <Button
          type="button"
          data-test-id="customer-manager-token-domain-reset-button"
          onClick={handleReset}
          disabled={isGenerating}
          text={t('common.reset')}
          variant="text"
        />
      </Stack>
      {query && (
        <Stack spacing={SIZE.XS}>
          {isFetching ? (
            <Spinner size={SIZE.M} />
          ) : isError ? (
            <Text variant="bodyRegular" color="DangerMain">
              {t('admin.systemAdminToken.error')}
            </Text>
          ) : (results?.content.length ?? 0) === 0 ? (
            <Text variant="bodyRegular">
              {t('admin.customerManagerToken.noResultsFound')}
            </Text>
          ) : (
            results?.content.map((domain) => (
              <Button
                type="button"
                key={domain.domainId}
                data-test-id={`customer-manager-token-domain-result-${domain.domainId}`}
                onClick={() => handleSelect(domain.domainId)}
                disabled={isGenerating || disabled}
                variant="text"
                text={domain.name}
              />
            ))
          )}
        </Stack>
      )}
    </Stack>
  );
};

const CustomerManagerToken = () => {
  const { t } = useTranslate();
  const { data: domainsCount } = useManageableDomainsCount();
  const tooManyDomains =
    domainsCount !== undefined &&
    domainsCount.totalElements > MAX_DOMAINS_FOR_DROPDOWN;
  const { data: domains } = useManageableDomains(!tooManyDomains);
  const {
    data: tokens,
    error: tokensError,
    isLoading: isTokensLoading,
  } = useCustomerManagerTokens();
  const { generateToken, generateTokenAsync, isPending } =
    useGenerateCustomerManagerToken();
  const { deleteToken } = useDeleteCustomerManagerToken();

  const requiresSso = tokensError?.response?.status === 403;
  const generationBlocked = isTokensLoading || requiresSso;

  const formMethods = useForm({
    defaultValues: { domainId: '' },
    schema,
  });

  const domainOptions = useMemo(
    () =>
      (domains?.content ?? []).map((domain) => ({
        label: domain.name,
        value: toDomainOptionValue(domain.domainId),
      })),
    [domains],
  );

  const handleSubmit = (values: z.infer<typeof schema>) => {
    generateToken(fromDomainOptionValue(values.domainId));
  };

  const handleCopy = (token: string) => navigator.clipboard.writeText(token);

  const columns = [
    {
      title: t('admin.customerManagerToken.tenant'),
      dataIndex: 'tenant',
      key: 'tenant',
      render: (_: unknown, row: ITokenRow) => (
        <TenantNameCell
          domainId={row.domainId}
          knownName={
            domains?.content.find((domain) => domain.domainId === row.domainId)
              ?.name
          }
        />
      ),
    },
    {
      title: t('admin.systemAdminToken.token'),
      dataIndex: 'token',
      key: 'token',
      render: (token: string) => (
        <Stack direction="row" spacing={SIZE.S} alignItems="center">
          {token}{' '}
          <Button
            onClick={() => handleCopy(token)}
            data-test-id="copy-button"
            aria-label={t('common.copy')}
            icon="copy"
            variant="text"
          />
        </Stack>
      ),
    },
    {
      title: t('admin.customerManagerToken.expiresAt'),
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (expiresAt: string) => formatDateTime(expiresAt),
    },
    {
      title: t('common.delete'),
      dataIndex: 'delete',
      key: 'delete',
      render: (_: unknown, row: ITokenRow) => (
        <Button
          onClick={() => deleteToken(row.domainId)}
          data-test-id="delete-button"
          aria-label={t('common.delete')}
          icon="delete"
          variant="text"
          color="error"
        />
      ),
    },
  ];

  const rows: ITokenRow[] = (tokens ?? []).map((activeToken) => ({
    key: activeToken.token,
    domainId: activeToken.domainId,
    token: activeToken.token,
    expiresAt: activeToken.expiresAt,
  }));

  return (
    <div className={styles.page}>
      <Text variant="titleStandard">
        {t('admin.customerManagerToken.title')}
      </Text>
      <Text variant="bodyRegular">
        {t('admin.customerManagerToken.description')}
      </Text>
      <Text variant="bodyRegular">
        <i>{t('admin.systemAdminToken.ssoNote')}</i>
      </Text>

      <Form methods={formMethods} onSubmit={handleSubmit}>
        {tooManyDomains ? (
          <TenantSearch
            onSelect={generateTokenAsync}
            disabled={generationBlocked}
          />
        ) : (
          <>
            <Form.Combobox
              data-test-id="customer-manager-token-domain-select"
              name="domainId"
              label={t('admin.customerManagerToken.tenant')}
              placeholder={t('admin.customerManagerToken.tenantPlaceholder')}
              options={domainOptions}
              allowFiltering
              required
            />
            <Button
              data-test-id="generate-token-button"
              type="submit"
              disabled={isPending || generationBlocked}
              text={t('admin.systemAdminToken.generateToken')}
            />
          </>
        )}
      </Form>

      {Boolean(tokensError) && (
        <Text variant="bodyRegular" color="DangerMain">
          {requiresSso
            ? t('admin.systemAdminToken.ssoRequired')
            : t('admin.systemAdminToken.error')}
        </Text>
      )}

      {rows.length > 0 && (
        <Table columns={columns} dataSource={rows} pagination={false} />
      )}
    </div>
  );
};

export default withAdminLayout(CustomerManagerToken);
